import { useState, useEffect, useCallback } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { Modal, Button, Input, PopOver, useMessage } from '@pepper/ui'

import { useCreateStatus, useUpdateStatusDetails } from 'utils/hooks/workflows'
import ShadeSelector from '../ShadeSelector/ShadeSelector'
import s from './AddEditStatusModal.module.css'

type Props = {
  statusList: WorkflowStatus[]
  companyId: string
  statusId?: string
  statusColor?: string
  initialValue?: string
  onSuccess?: (status: WorkflowStatus) => void
}

export const AddEditStatusModal = NiceModal.create<Props>(
  ({
    statusList,
    companyId,
    statusId,
    statusColor,
    initialValue,
    onSuccess,
  }: Props) => {
    const [showColorPicker, setShowColorPicker] = useState(false)
    const [newStatus, setNewStatus] = useState('')
    const [color, selectColor] = useState('')
    const modal = useModal()
    const { showSuccess, showError } = useMessage()
    const queryClient = useQueryClient()
    const { mutateAsync: createStatus } = useCreateStatus()
    const { mutateAsync: updateStatus } = useUpdateStatusDetails()

    const chooseDefaultColor = useCallback(() => {
      const currColorCount: Record<string, number> = {
        '#F43F5E': 0,
        '#EF4444': 0,
        '#F97316': 0,
        '#EAB308': 0,
        '#84CC16': 0,
        '#22C55E': 0,
        '#10B981': 0,
        '#14B8A6': 0,
        '#06B6D4': 0,
        '#0EA5E9': 0,
        '#3B82F6': 0,
        '#6366F1': 0,
        '#8B5CF6': 0,
        '#D946EF': 0,
        '#EC4899': 0,
        '#1E293B': 0,
      }
      for (const status of statusList) {
        if (currColorCount[`#${status.color.toUpperCase()}`]) {
          currColorCount[`#${status.color.toUpperCase()}`] += 1
        }
      }
      if (statusColor) {
        selectColor(`#${statusColor}`)
        return
      }
      const hexCodes = Object.values(currColorCount)
      const colorKeys = Object.keys(currColorCount)
      const maxCount = Math.max(...hexCodes)
      let allValSame = true
      for (const colorKey of colorKeys) {
        if (currColorCount[colorKey] !== maxCount) {
          selectColor(colorKey)
          allValSame = false
          break
        }
      }
      if (allValSame) {
        selectColor('#F43F5E')
      }
    }, [statusColor, statusList])

    const editStatus = async () => {
      let response
      if (newStatus === initialValue) {
        response = await updateStatus({
          statusId,
          color: color.replace('#', ''),
        })
      } else {
        response = await updateStatus({
          statusId,
          name: newStatus,
          color: color.replace('#', ''),
        })
      }
      if (response.status === 'success') {
        showSuccess('Status updated successfully!')
      } else {
        showError('Something went wrong!')
      }
    }

    const addStatus = async () => {
      const response = await createStatus({
        companyId,
        name: newStatus,
        color: color.replace('#', ''),
      })
      if (response.status === 'success') {
        await queryClient.invalidateQueries({
          queryKey: ['workflow-statuses', companyId],
        })
        onSuccess?.(response.data)
        showSuccess('Status added successfully!')
      } else {
        showError('Something went wrong!')
      }
    }

    const handleSubmit = async () => {
      if (initialValue) {
        await editStatus()
      } else {
        await addStatus()
      }
      queryClient.invalidateQueries(['workflow-statuses', companyId])
      modal.remove()
    }

    useEffect(() => {
      chooseDefaultColor()
    }, [chooseDefaultColor])

    useEffect(() => {
      initialValue && setNewStatus(initialValue)
    }, [initialValue])

    const isExisting = statusList.some(item => {
      if (initialValue)
        return (
          item.name.toLowerCase() === newStatus.toLowerCase().trim() &&
          newStatus.toLowerCase().trim() !== initialValue.toLowerCase().trim()
        )
      else return item.name.toLowerCase() === newStatus.toLowerCase().trim()
    })

    return (
      <Modal
        visible={modal.visible}
        onCancel={modal.remove}
        onOk={handleSubmit}
        okText="Save"
        okButtonProps={{
          disabled: isExisting,
        }}
        cancelText="Cancel"
        title={!initialValue ? 'Add New Status' : 'Edit Status'}
        centered={true}
        maskClosable={false}
        className={s.modal}
      >
        <div className={s.modalBody}>
          <PopOver
            visible={showColorPicker}
            onVisibleChange={() => {
              if (showColorPicker) setShowColorPicker(false)
            }}
            content={
              <ShadeSelector selectColor={selectColor} defaultColor={color} />
            }
            trigger="click"
            placement="bottomLeft"
            overlayClassName={s.shadePopover}
          >
            <Button
              className={s.colorPickerButton}
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              <div
                className={clsx(s.shade)}
                style={{ backgroundColor: color }}
              />
              <Icon icon="icon-park-solid:down-one" />
            </Button>
          </PopOver>
          <Input
            placeholder="Status Name"
            className={clsx(s.inputBox, {
              [s.statusInput]: isExisting,
            })}
            defaultValue={initialValue}
            onChange={event => setNewStatus(event.target.value)}
            autoFocus={true}
          />
        </div>
        {isExisting && (
          <div className={s.duplicateWarning}>
            <Icon icon="lucide:x-circle" className={s.duplicateX} />
            This status already exists
          </div>
        )}
      </Modal>
    )
  }
)
